import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { getDepartmentsForOrderStatusTrackerToLocation } from '@/features/auditDashboard/auditApi';
import { getWorkOrderInfo } from '@/features/search/searchApi';
import { useSiteContext } from '@/lib/useSiteContext';
import { usePropertyStore } from '@/store/propertyStore';
import { getOrderStatusDestination } from './orderStatusDestination';

/**
 * Classify every order-status navigation. Query-string order numbers are
 * resolved through WorkOrderInfo; links without one use the selected order.
 */
export default function OrderStatusTrackerRedirect({ children }: { children: ReactNode }) {
  // Preserve the original OrderStatusTracker route's sidebar initialization.
  // Eligible orders redirect before the tracker component mounts, so its own
  // useSiteContext('workOrder') call would otherwise never run.
  useSiteContext('workOrder');
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const selectedWorkOrder = usePropertyStore((state) => state.workOrder);
  const queryEntries = Array.from(searchParams.entries());
  const getParam = (name: string) =>
    queryEntries.find(([key]) => key.toLowerCase() === name.toLowerCase())?.[1] ?? null;
  const isWds = getParam('Iswds')?.toLowerCase() === 'true';
  const forceOrderStatusTracker = getParam('forceOrderStatusTracker')?.toLowerCase() === 'true';
  const itemTypeRecordId = getParam('ItemTypeRecordId');
  const queryWorkOrder = getParam('workorder');
  const orderNumber = isWds ? itemTypeRecordId : queryWorkOrder;
  const selectedDepartmentForLookup = orderNumber ? undefined : selectedWorkOrder?.department;
  const selectedStateCodeForLookup = orderNumber
    ? undefined
    : selectedWorkOrder?.workflowStateTransition?.currentState?.code;
  const hasClassificationInput =
    !forceOrderStatusTracker && Boolean(orderNumber || selectedDepartmentForLookup);
  const resolutionKey =
    orderNumber ??
    `selected:${selectedDepartmentForLookup ?? ''}:${selectedStateCodeForLookup ?? ''}`;
  const [resolution, setResolution] = useState<{
    key: string;
    destination: string;
  } | null>(null);

  useEffect(() => {
    if (!hasClassificationInput) return;

    let cancelled = false;
    const workOrderPromise = orderNumber
      ? getWorkOrderInfo(orderNumber)
      : Promise.resolve({
          department: selectedDepartmentForLookup,
          workflowStateTransition: { currentState: { code: selectedStateCodeForLookup } },
        });

    Promise.all([workOrderPromise, getDepartmentsForOrderStatusTrackerToLocation()])
      .then(([workOrder, geomapDepartments]) => {
        if (!cancelled) {
          setResolution({
            key: resolutionKey,
            destination: getOrderStatusDestination(
              workOrder?.department,
              geomapDepartments,
              workOrder?.workflowStateTransition?.currentState?.code
            ),
          });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setResolution({
            key: resolutionKey,
            destination: '/Audit/auditdashboard/orderStatusTracker',
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    hasClassificationInput,
    orderNumber,
    resolutionKey,
    selectedDepartmentForLookup,
    selectedStateCodeForLookup,
  ]);

  const destination = !hasClassificationInput
    ? '/Audit/auditdashboard/orderStatusTracker'
    : resolution?.key === resolutionKey
      ? resolution.destination
      : null;

  if (destination === '/Location/geomap') {
    if (orderNumber) searchParams.set('workorder', orderNumber);
    return <Navigate to={{ pathname: destination, search: searchParams.toString() }} replace />;
  }

  if (destination && isWds && itemTypeRecordId && !queryWorkOrder) {
    searchParams.set('workorder', itemTypeRecordId);
    return <Navigate to={{ search: searchParams.toString() }} replace />;
  }

  return destination ? children : null;
}
